import React, { useState, useEffect, useRef } from 'react'
import './SupportMain.css'
import TeamHeader from '../TeamHeader/TeamHeader'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

// Auth
import { useAuth } from '../../Auth';

// Icons
import { FaBug } from "react-icons/fa";
import { PiShootingStarBold } from "react-icons/pi";
import { FaAngleDoubleUp } from "react-icons/fa";
import { GrAnnounce } from "react-icons/gr";
import { FaQuestionCircle } from "react-icons/fa";
import { FaRegCircle } from "react-icons/fa";
import { FiEdit2, FiTrash2, FiPlus, FiX, FiSave } from "react-icons/fi";

// ─── API Helpers ──────────────────────────────────────────────────────────────

const LOCAL_MODE = false;

function apiUrl(path: string): string {
    return LOCAL_MODE ? `http://localhost:8000${path}` : path;
}

function credentials(): RequestCredentials {
    return LOCAL_MODE ? "include" : "same-origin";
}

const fetchCsrfToken = async () => {
    const response = await fetch(apiUrl('/api/csrf-token/'), {
        method: 'GET',
        credentials: credentials(),
    });
    if (response.ok) {
        const data = await response.json();
        return data.csrfToken;
    }
    return null;
};

function formatUpdateContent(content: string) {
    let formatted = content

    // Bold: b[text]
    formatted = formatted.replace(
        /b\[(.*?)\]/g,
        '<strong>$1</strong>'
    )

    // Underline: u[text]
    formatted = formatted.replace(
        /u\[(.*?)\]/g,
        '<span class="update-underline">$1</span>'
    )

    // Hyperlink: [text]*(url)
    formatted = formatted.replace(
        /\[(.*?)\]\*\((.*?)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer" class="update-link">$1</a>'
    )

    // Line breaks
    formatted = formatted.replace(/\n/g, '<br />')

    return formatted
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface Attachment {
    attachment_id: number
    file_url: string
}

interface Ticket {
    ticket_id: number
    title: string
    description: string
    ticket_type: string
    ticket_topic: string
    status: string
    priority: string
    department: string | null
    created_by: number
    created_by_name: string
    assigned_to: number | null
    assigned_to_name: string | null
    resolution_notes: string | null
    created_at: string
    resolved_at: string | null
    attachments: Attachment[]
}

interface TeamUpdate {
    update_id: number
    title: string
    content: string
    update_type: string
    is_general: boolean
    target_department: string | null
    pinned: boolean
    created_at: string
}

const TICKET_TYPES = ['Bug', 'Feature', 'Improvement', 'Question', 'Other']
const TICKET_TOPICS = ['Website', 'Customer Portal', 'Team Portal', 'Backend', 'Server']
const STATUSES = ['Submitted', 'In Progress', 'Resolved', 'Closed']
const PRIORITIES = ['Low', 'Medium', 'High', 'Critical']
const UPDATE_TYPES = ['Bug Fix', 'New Feature', 'Adjustment', 'Announcement']

const STATUS_COLORS: Record<string, string> = {
    'Submitted': 'status-submitted',
    'In Progress': 'status-inprogress',
    'Resolved': 'status-resolved',
    'Closed': 'status-closed',
}

const PRIORITY_COLORS: Record<string, string> = {
    'Low': 'priority-low',
    'Medium': 'priority-medium',
    'High': 'priority-high',
    'Critical': 'priority-critical',
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
    'Bug Fix': <FaBug />,
    'New Feature': <PiShootingStarBold />,
    'Improvement': <FaAngleDoubleUp />,
    'Question': <FaQuestionCircle />,
    'Announcement': <GrAnnounce />,
    'Other': <FaRegCircle />,
    'Adjustment': <FaAngleDoubleUp />,
}

const DEPARTMENTS = [
    'Detection', 'GIS', 'Hardware', 'Manufacturing',
    'Server', 'AI', 'Marketing', 'Account',
]

// ─── Utility ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
    })
}

function timeAgo(iso: string) {
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60000)
    const hours = Math.floor(mins / 60)
    const days = Math.floor(hours / 24)
    if (days > 0) return `${days}d ago`
    if (hours > 0) return `${hours}h ago`
    if (mins > 0) return `${mins}m ago`
    return 'just now'
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Badge({ label, className }: { label: string; className?: string }) {
    return <span className={`badge ${className ?? ''}`}>{label}</span>
}

// ─── Confirm Delete Modal ──────────────────────────────────────────────────────

function ConfirmDeleteModal({
    message,
    onConfirm,
    onCancel,
}: {
    message: string
    onConfirm: () => void
    onCancel: () => void
}) {
    return (
        <div className="modal-overlay" onClick={onCancel}>
            <div className="modal-box confirm-modal" onClick={e => e.stopPropagation()}>
                <h3 className="modal-title">Confirm Delete</h3>
                <p className="confirm-message">{message}</p>
                <div className="modal-actions">
                    <button className="btn-ghost" onClick={onCancel}>Cancel</button>
                    <button className="btn-danger" onClick={onConfirm}>Delete</button>
                </div>
            </div>
        </div>
    )
}

// ─── Ticket Row ───────────────────────────────────────────────────────────────

function TicketRow({
    ticket,
    onClick,
    isAdmin,
    username,
    first_name,
    last_name,
    onEdit,
    onDelete,
}: {
    ticket: Ticket
    onClick: () => void
    isAdmin: boolean
    username: string
    first_name: string
    last_name: string
    onEdit: (t: Ticket) => void
    onDelete: (t: Ticket) => void
}) {
    const isOwner = (ticket: Ticket): boolean => {
        if (!ticket.created_by_name) return false
        const name = ticket.created_by_name.toLowerCase().trim()

        // Full name match (what the serializer returns when first/last are set)
        if (first_name || last_name) {
            const fullName = `${first_name ?? ''} ${last_name ?? ''}`.toLowerCase().trim()
            if (fullName && name === fullName) return true
        }

        // Fallback: username (serializer uses this when no first/last name)
        if (username && name === username.toLowerCase().trim()) return true

        return false
    }
    const canEdit = isAdmin || (isOwner(ticket) && ticket.status === 'Submitted')

    return (
        <div className="ticket-row" onClick={onClick}>
            <div className="ticket-row-left">
                <span className="ticket-id">#{ticket.ticket_id}</span>
                <div className="ticket-row-meta">
                    <span className="ticket-title">{ticket.title}</span>
                    <div className="ticket-row-tags">
                        <Badge label={ticket.ticket_type} className="badge-type" />
                        <Badge label={ticket.ticket_topic} className="badge-type" />
                        {ticket.department && (
                            <Badge label={ticket.department} className="badge-dept" />
                        )}
                    </div>
                </div>
            </div>

            <div className="ticket-row-right">
                <Badge label={ticket.priority} className={PRIORITY_COLORS[ticket.priority]} />
                <Badge label={ticket.status} className={STATUS_COLORS[ticket.status]} />
                <span className="ticket-time">{timeAgo(ticket.created_at)}</span>

                {/* Action buttons — stop propagation so row click doesn't fire */}
                <div className="row-actions" onClick={e => e.stopPropagation()}>
                    {canEdit && (
                        <button
                            className="row-action-btn edit-btn"
                            title="Edit ticket"
                            onClick={() => onEdit(ticket)}
                        >
                            <FiEdit2 size={13} />
                        </button>
                    )}
                    {isAdmin && (
                        <button
                            className="row-action-btn delete-btn"
                            title="Delete ticket"
                            onClick={() => onDelete(ticket)}
                        >
                            <FiTrash2 size={13} />
                        </button>
                    )}
                </div>

                <span className="ticket-chevron">›</span>
            </div>
        </div>
    )
}

// ─── Ticket Edit Modal ────────────────────────────────────────────────────────

function TicketEditModal({
    ticket,
    isAdmin,
    onClose,
    onSaved,
}: {
    ticket: Ticket
    isAdmin: boolean
    onClose: () => void
    onSaved: () => void
}) {
    const [form, setForm] = useState({
        title: ticket.title,
        description: ticket.description,
        ticket_type: ticket.ticket_type,
        ticket_topic: ticket.ticket_topic,
        priority: ticket.priority,
        status: ticket.status,
        department: ticket.department ?? '',
        resolution_notes: ticket.resolution_notes ?? '',
    })
    const [files, setFiles] = useState<File[]>([])
    const [saving, setSaving] = useState(false)
    const fileRef = useRef<HTMLInputElement>(null)

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
    }

    const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) setFiles(Array.from(e.target.files))
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            const csrfToken = await fetchCsrfToken()
            if (!csrfToken) { toast.error('CSRF error'); return }

            // Use FormData so we can attach files alongside JSON fields
            const fd = new FormData()
            fd.append('title', form.title)
            fd.append('description', form.description)
            fd.append('ticket_type', form.ticket_type)
            fd.append('ticket_topic', form.ticket_topic)

            if (isAdmin) {
                fd.append('priority', form.priority)
                fd.append('status', form.status)
                if (form.department) fd.append('department', form.department)
                if (form.resolution_notes) fd.append('resolution_notes', form.resolution_notes)
            }

            files.forEach(f => fd.append('attachments', f))

            const res = await fetch(apiUrl(`/portal/api/support/tickets/${ticket.ticket_id}/`), {
                method: 'PATCH',
                credentials: credentials(),
                headers: {
                    // No Content-Type — let the browser set multipart boundary
                    'X-CSRFToken': csrfToken,
                },
                body: fd,
            })

            if (res.ok) {
                toast.success('Ticket updated.')
                onSaved()
                onClose()
            } else {
                toast.error('Failed to update ticket.')
            }
        } catch {
            toast.error('Failed to update ticket.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box ticket-edit-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3 className="modal-title">
                        Edit Ticket <span className="modal-id">#{ticket.ticket_id}</span>
                    </h3>
                    <button className="modal-close-btn" onClick={onClose}><FiX /></button>
                </div>

                <div className="modal-body">
                    <div className="form-field">
                        <label>Title</label>
                        <input name="title" value={form.title} onChange={handleChange} />
                    </div>

                    <div className="form-field">
                        <label>Description</label>
                        <textarea name="description" rows={3} value={form.description} onChange={handleChange} />
                    </div>

                    <div className="form-grid-2">

                        <div className="form-field">
                            <label>Type</label>
                            <select name="ticket_type" value={form.ticket_type} onChange={handleChange}>
                                {TICKET_TYPES.map(t => <option key={t}>{t}</option>)}
                            </select>
                        </div>

                        <div className="form-field">
                            <label>Topic</label>
                            <select name="ticket_topic" value={form.ticket_topic} onChange={handleChange}>
                                {TICKET_TOPICS.map(t => <option key={t}>{t}</option>)}
                            </select>
                        </div>

                        <div className="form-field">
                            <label>Department <span className="optional">(optional)</span></label>
                            <select name="department" value={form.department} onChange={handleChange}>
                                <option value="">— None —</option>
                                {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
                            </select>
                        </div>

                        <div className="form-field">
                            <label>Priority</label>
                            <select name="priority" value={form.priority} onChange={handleChange}>
                                {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                            </select>
                        </div>

                    </div>

                    {isAdmin && (


                        <div className="form-field">
                            <label>Status</label>
                            <select name="status" value={form.status} onChange={handleChange}>
                                {STATUSES.map(s => <option key={s}>{s}</option>)}
                            </select>
                        </div>

                    )}

                    {isAdmin && (
                        <div className="form-field">
                            <label>Resolution Notes <span className="optional">(optional)</span></label>
                            <textarea
                                name="resolution_notes"
                                rows={3}
                                value={form.resolution_notes}
                                onChange={handleChange}
                                placeholder="Add resolution details…"
                            />
                        </div>
                    )}

                    {/* Existing attachments */}
                    {ticket.attachments.length > 0 && (
                        <div className="form-field">
                            <label>Existing Attachments</label>
                            <div className="attachment-list">
                                {ticket.attachments.map(a => (
                                    <a
                                        key={a.attachment_id}
                                        href={a.file_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="attachment-chip"
                                    >
                                        <span className="attachment-icon">⊞</span>
                                        File {a.attachment_id}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* New attachments */}
                    <div className="form-field">
                        <label>
                            Add Attachments <span className="optional">(optional)</span>
                        </label>
                        <div
                            className="file-drop-zone"
                            onClick={() => fileRef.current?.click()}
                            onDragOver={e => e.preventDefault()}
                            onDrop={e => { e.preventDefault(); setFiles(Array.from(e.dataTransfer.files)) }}
                        >
                            {files.length > 0
                                ? <span className="file-list">{files.map(f => f.name).join(', ')}</span>
                                : <span className="file-placeholder">Drop files here or click to browse</span>
                            }
                        </div>
                        <input
                            ref={fileRef}
                            type="file"
                            multiple
                            onChange={handleFiles}
                            style={{ display: 'none' }}
                        />
                    </div>
                </div>

                <div className="modal-actions">
                    <button className="btn-ghost" onClick={onClose}>Cancel</button>
                    <button className="btn-primary" onClick={handleSave} disabled={saving}>
                        <FiSave size={13} />
                        {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    )
}

// ─── Ticket Detail Panel ───────────────────────────────────────────────────────

function TicketDetailPanel({
    ticket,
    onClose,
}: {
    ticket: Ticket
    onClose: () => void
}) {
    return (
        <div className="detail-panel">
            <div className="detail-panel-header">
                <div>
                    <span className="ticket-id large">#{ticket.ticket_id}</span>
                    <h2 className="detail-title">{ticket.title}</h2>
                </div>
                <button className="close-btn" onClick={onClose}>✕</button>
            </div>

            <div className="detail-badges">
                <Badge label={ticket.ticket_type} className="badge-type" />
                <Badge label={ticket.ticket_topic} className="badge-type" />
                <Badge label={ticket.priority} className={PRIORITY_COLORS[ticket.priority]} />
                <Badge label={ticket.status} className={STATUS_COLORS[ticket.status]} />
                {ticket.department && <Badge label={ticket.department} className="badge-dept" />}
            </div>

            <div className="detail-meta-grid">
                <div className="meta-item">
                    <span className="meta-label">Submitted by</span>
                    <span>{ticket.created_by_name}</span>
                </div>
                <div className="meta-item">
                    <span className="meta-label">Created</span>
                    <span>{formatDate(ticket.created_at)}</span>
                </div>
                {ticket.assigned_to_name && (
                    <div className="meta-item">
                        <span className="meta-label">Assigned to</span>
                        <span>{ticket.assigned_to_name}</span>
                    </div>
                )}
                {ticket.resolved_at && (
                    <div className="meta-item">
                        <span className="meta-label">Resolved</span>
                        <span>{formatDate(ticket.resolved_at)}</span>
                    </div>
                )}
            </div>

            <div className="detail-section">
                <span className="detail-section-label">Description</span>
                <p className="detail-description">{ticket.description}</p>
            </div>

            {ticket.resolution_notes && (
                <div className="detail-section resolution-section">
                    <span className="detail-section-label">Resolution Notes</span>
                    <p className="detail-description">{ticket.resolution_notes}</p>
                </div>
            )}

            {ticket.attachments.length > 0 && (
                <div className="detail-section">
                    <span className="detail-section-label">
                        Attachments ({ticket.attachments.length})
                    </span>
                    <div className="attachment-list">
                        {ticket.attachments.map(a => (
                            <a
                                key={a.attachment_id}
                                href={a.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="attachment-chip"
                            >
                                <span className="attachment-icon">⊞</span>
                                File {a.attachment_id}
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

// ─── New Ticket Form ───────────────────────────────────────────────────────────

function NewTicketForm({ onSuccess }: { onSuccess: () => void }) {
    const [form, setForm] = useState({
        title: '',
        description: '',
        ticket_type: 'Bug',
        ticket_topic: 'Website',
        priority: 'Medium',
        department: '',
    })
    const [files, setFiles] = useState<File[]>([])
    const [submitting, setSubmitting] = useState(false)
    const fileRef = useRef<HTMLInputElement>(null)

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
    }

    const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) setFiles(Array.from(e.target.files))
    }

    const handleSubmit = async () => {
        if (!form.title.trim() || !form.description.trim()) {
            toast.error('Title and description are required.')
            return
        }
        setSubmitting(true)
        try {
            const csrfToken = await fetchCsrfToken()
            if (!csrfToken) { toast.error('Failed to get CSRF token.'); return }

            const fd = new FormData()
            Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v) })
            files.forEach(f => fd.append('attachments', f))

            const response = await fetch(apiUrl('/portal/api/support/tickets/'), {
                method: 'POST',
                credentials: credentials(),
                headers: { 'X-CSRFToken': csrfToken },
                body: fd,
            })

            if (response.ok) {
                toast.success('Ticket submitted!')
                setForm({ title: '', description: '', ticket_type: 'Bug', ticket_topic: 'Website', priority: 'Medium', department: '' })
                setFiles([])
                onSuccess()
            } else {
                toast.error('Failed to submit ticket.')
            }
        } catch {
            toast.error('Failed to submit ticket.')
        } finally {
            setSubmitting(false)
        }
    }

    return (

        <div className="new-ticket-form-wrapper">
            <div className="new-ticket-form">
                <h3 className="form-heading">Submit a New Ticket</h3>

                <div className="form-grid-2">
                    <div className="form-field">
                        <label>Ticket Type</label>
                        <select name="ticket_type" value={form.ticket_type} onChange={handleChange}>
                            {TICKET_TYPES.map(t => <option key={t}>{t}</option>)}
                        </select>
                    </div>

                    <div className="form-field">
                        <label>Ticket Topic</label>
                        <select name="ticket_topic" value={form.ticket_topic} onChange={handleChange}>
                            {TICKET_TOPICS.map(t => <option key={t}>{t}</option>)}
                        </select>
                    </div>

                    <div className="form-field">
                        <label>Priority</label>
                        <select name="priority" value={form.priority} onChange={handleChange}>
                            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                        </select>
                    </div>

                    <div className="form-field">
                        <label>Department <span className="optional">(optional)</span></label>
                        <select name="department" value={form.department} onChange={handleChange}>
                            <option value="">— None —</option>
                            {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
                        </select>
                    </div>

                </div>

                <div className="form-field">
                    <label>Title</label>
                    <input name="title" placeholder="Brief summary of the issue…" value={form.title} onChange={handleChange} />
                </div>

                <div className="form-field">
                    <label>Description</label>
                    <textarea name="description" placeholder="Provide as much detail as possible..." rows={4} value={form.description} onChange={handleChange} />
                </div>

                <div className="form-field">
                    <label>Attachments <span className="optional">(optional)</span></label>
                    <div
                        className="file-drop-zone"
                        onClick={() => fileRef.current?.click()}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => { e.preventDefault(); setFiles(Array.from(e.dataTransfer.files)) }}
                    >
                        {files.length > 0
                            ? <span className="file-list">{files.map(f => f.name).join(', ')}</span>
                            : <span className="file-placeholder">Drop files here or click to browse</span>
                        }
                    </div>
                    <input ref={fileRef} type="file" multiple onChange={handleFiles} style={{ display: 'none' }} />
                </div>

                <button className="spp-submit-btn" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit Ticket →'}
                </button>
            </div>
        </div>
    )
}

// ─── Update Card ───────────────────────────────────────────────────────────────

const UPDATE_TYPE_COLORS: Record<string, string> = {
    'Bug Fix': 'upd-bugfix',
    'New Feature': 'upd-feature',
    'Improvement': 'upd-improvement',
    'Announcement': 'upd-announcement',
    'Adjustment': 'upd-maintenance',
}

function UpdateCard({
    update,
    isAdmin,
    onEdit,
    onDelete,
}: {
    update: TeamUpdate
    isAdmin: boolean
    onEdit: (u: TeamUpdate) => void
    onDelete: (u: TeamUpdate) => void
}) {
    const [expanded, setExpanded] = useState(false)

    return (
        <div className={`update-card ${update.pinned ? 'pinned' : ''}`}>
            <div className="update-card-header" onClick={() => setExpanded(e => !e)}>
                <div className="update-card-left">
                    <span className="update-type-icon">
                        {TYPE_ICONS[update.update_type] ?? '•'}
                    </span>
                    <div>
                        <div className="update-card-title">
                            {update.pinned && <span className="pin-icon">📌 </span>}
                            {update.title}
                        </div>
                        <div className="update-card-sub">
                            <Badge label={update.update_type} className={UPDATE_TYPE_COLORS[update.update_type] ?? ''} />
                            <span className="update-time">{timeAgo(update.created_at)}</span>
                        </div>
                    </div>
                </div>

                <div className="update-card-right" onClick={e => e.stopPropagation()}>
                    {isAdmin && (
                        <div className="row-actions">
                            <button
                                className="row-action-btn edit-btn"
                                title="Edit update"
                                onClick={() => onEdit(update)}
                            >
                                <FiEdit2 size={13} />
                            </button>
                            <button
                                className="row-action-btn delete-btn"
                                title="Delete update"
                                onClick={() => onDelete(update)}
                            >
                                <FiTrash2 size={13} />
                            </button>
                        </div>
                    )}
                    <span className={`expand-chevron ${expanded ? 'open' : ''}`}>›</span>
                </div>
            </div>

            {expanded && (
                <div className="update-card-body">
                    <p
                        dangerouslySetInnerHTML={{
                            __html: formatUpdateContent(update.content)
                        }}
                    />
                    <span className="update-date">
                        {formatDate(update.created_at)}
                        {!update.is_general && update.target_department && (
                            <Badge label={update.target_department} className="badge-dept" />
                        )}
                    </span>
                </div>
            )}
        </div>
    )
}

// ─── Update Edit/Create Modal ──────────────────────────────────────────────────

function UpdateModal({
    update,
    onClose,
    onSaved,
}: {
    update: TeamUpdate | null   // null = create mode
    onClose: () => void
    onSaved: () => void
}) {
    const isCreate = update === null
    const [form, setForm] = useState({
        title: update?.title ?? '',
        content: update?.content ?? '',
        update_type: update?.update_type ?? 'Announcement',
        is_general: update?.is_general ?? true,
        target_department: update?.target_department ?? '',
        pinned: update?.pinned ?? false,
    })
    const [saving, setSaving] = useState(false)

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const target = e.target as HTMLInputElement
        const value = target.type === 'checkbox' ? target.checked : target.value
        setForm(prev => ({ ...prev, [target.name]: value }))
    }

    const handleSave = async () => {
        if (!form.title.trim() || !form.content.trim()) {
            toast.error('Title and content are required.')
            return
        }
        setSaving(true)
        try {
            const csrfToken = await fetchCsrfToken()
            if (!csrfToken) { toast.error('CSRF error'); return }

            const payload = {
                title: form.title,
                content: form.content,
                update_type: form.update_type,
                is_general: form.is_general,
                target_department: form.is_general ? null : (form.target_department || null),
                pinned: form.pinned,
            }

            const url = isCreate
                ? apiUrl('/portal/api/support/updates/')
                : apiUrl(`/portal/api/support/updates/${update!.update_id}/`)

            const res = await fetch(url, {
                method: isCreate ? 'POST' : 'PATCH',
                credentials: credentials(),
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify(payload),
            })

            if (res.ok) {
                toast.success(isCreate ? 'Update posted.' : 'Update saved.')
                onSaved()
                onClose()
            } else {
                toast.error('Failed to save update.')
            }
        } catch {
            toast.error('Failed to save update.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box update-edit-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3 className="modal-title">
                        {isCreate ? 'New Team Update' : `Edit Update #${update!.update_id}`}
                    </h3>
                    <button className="modal-close-btn" onClick={onClose}><FiX /></button>
                </div>

                <div className="modal-body">
                    <div className="form-field">
                        <label>Title</label>
                        <input name="title" value={form.title} onChange={handleChange} placeholder="Update title…" />
                    </div>

                    <div className="form-field">
                        <label>Content</label>
                        <textarea name="content" rows={5} value={form.content} onChange={handleChange} placeholder="Write the update content…" />
                    </div>

                    <div className="form-grid-2">
                        <div className="form-field">
                            <label>Type</label>
                            <select name="update_type" value={form.update_type} onChange={handleChange}>
                                {UPDATE_TYPES.map(t => <option key={t}>{t}</option>)}
                            </select>
                        </div>

                        <div className="form-field">
                            <label>Audience</label>
                            <select
                                name="is_general"
                                value={form.is_general ? 'true' : 'false'}
                                onChange={e => setForm(prev => ({ ...prev, is_general: e.target.value === 'true' }))}
                            >
                                <option value="true">General (all)</option>
                                <option value="false">Specific department</option>
                            </select>
                        </div>
                    </div>

                    {!form.is_general && (
                        <div className="form-field">
                            <label>Target Department</label>
                            <select name="target_department" value={form.target_department} onChange={handleChange}>
                                <option value="">— Select —</option>
                                {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
                            </select>
                        </div>
                    )}

                    <div className="form-field spp-checkbox-field">
                        <label className="spp-checkbox-label">
                            <input
                                type="checkbox"
                                name="pinned"
                                checked={form.pinned}
                                onChange={handleChange}
                            />

                            <span className={`lmp-list-check ${form.pinned ? "lmp-checked" : ""}`}>
                                {form.pinned && (
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                        <path
                                            d="M2 5L4 7L8 3"
                                            stroke="white"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                )}
                            </span>

                            <span>Pin this update</span>
                        </label>
                    </div>
                </div>

                <div className="modal-actions">
                    <button className="btn-ghost" onClick={onClose}>Cancel</button>
                    <button className="btn-primary" onClick={handleSave} disabled={saving}>
                        <FiSave size={13} />
                        {saving ? 'Saving…' : isCreate ? 'Post Update' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    )
}

// ─── Updates Tab ───────────────────────────────────────────────────────────────

function UpdatesTab({
    updates,
    isAdmin,
    onEdit,
    onDelete,
    onNew,
}: {
    updates: TeamUpdate[]
    isAdmin: boolean
    onEdit: (u: TeamUpdate) => void
    onDelete: (u: TeamUpdate) => void
    onNew: () => void
}) {
    const depts = Array.from(
        new Set(
            updates
                .filter(u => !u.is_general && u.target_department)
                .map(u => u.target_department as string)
        )
    )
    const tabs = ['General', ...depts]
    const [activeTab, setActiveTab] = useState('General')

    const filtered = updates.filter(u =>
        activeTab === 'General'
            ? u.is_general
            : !u.is_general && u.target_department === activeTab
    )

    return (
        <div className="updates-tab">
            <div className="updates-toolbar">
                <div className="dept-tabs">
                    {tabs.map(t => (
                        <button
                            key={t}
                            className={`dept-tab ${activeTab === t ? 'active' : ''}`}
                            onClick={() => setActiveTab(t)}
                        >
                            {t}
                            <span className="dept-tab-count">
                                {updates.filter(u =>
                                    t === 'General'
                                        ? u.is_general
                                        : (!u.is_general && u.target_department === t)
                                ).length}
                            </span>
                        </button>
                    ))}
                </div>

                {isAdmin && (
                    <button className="new-update-btn" onClick={onNew}>
                        <FiPlus size={14} />
                        New Update
                    </button>
                )}
            </div>

            <div className="updates-list">
                {filtered.length === 0 ? (
                    <div className="empty-state">No updates for this department yet.</div>
                ) : (
                    filtered.map(u => (
                        <UpdateCard
                            key={u.update_id}
                            update={u}
                            isAdmin={isAdmin}
                            onEdit={onEdit}
                            onDelete={onDelete}
                        />
                    ))
                )}
            </div>
        </div>
    )
}

// ─── Main Component ────────────────────────────────────────────────────────────

type MainTab = 'tickets' | 'updates'
type TicketsView = 'list' | 'new'

const SupportMain = () => {
    const [mainTab, setMainTab] = useState<MainTab>('tickets')
    const [ticketsView, setTicketsView] = useState<TicketsView>('list')
    const [tickets, setTickets] = useState<Ticket[]>([])
    const [updates, setUpdates] = useState<TeamUpdate[]>([])
    const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null)
    const [loading, setLoading] = useState(false)
    const [filterStatus, setFilterStatus] = useState<string>('All')

    // Modal state
    const [editingTicket, setEditingTicket] = useState<Ticket | null>(null)
    const [deletingTicket, setDeletingTicket] = useState<Ticket | null>(null)
    const [editingUpdate, setEditingUpdate] = useState<TeamUpdate | null | 'new'>(null)
    const [deletingUpdate, setDeletingUpdate] = useState<TeamUpdate | null>(null)

    // Auth / permissions
    const { isAuthenticated, getPermissions, username, first_name, last_name } = useAuth();
    const [permissionGroups, setPermissionGroups] = useState<{ [key: string]: any }>({});

    const isAdmin = permissionGroups?.SoftwareManager === true

    useEffect(() => {
        const fetchPermissions = async () => {
            const permissions = await getPermissions();
            setPermissionGroups(permissions?.groups || permissions || {});
        };
        if (isAuthenticated) fetchPermissions();
    }, [isAuthenticated, getPermissions]);

    const [unreadUpdates, setUnreadUpdates] = useState<number>(0)

    const fetchUnreadUpdates = async () => {
        try {
            const res = await fetch(
                apiUrl('/portal/api/support/updates/unread/'),
                {
                    method: 'GET',
                    credentials: credentials(),
                }
            )

            if (res.ok) {
                const data = await res.json()
                setUnreadUpdates(data.unread_count ?? 0)
            }
        } catch {
            // silently fail
        }
    }

    useEffect(() => {
        fetchUnreadUpdates()
    }, [])

    const markUpdatesRead = async () => {
        try {
            const csrfToken = await fetchCsrfToken()
            if (!csrfToken) return
            await fetch(apiUrl('/portal/api/support/updates/mark-read/'), {
                method: 'POST',
                credentials: credentials(),
                headers: { 'X-CSRFToken': csrfToken },
            })
        } catch {
            // silently fail
        }
    }

    useEffect(() => {
        if (mainTab === 'updates') {
            markUpdatesRead()
            setUnreadUpdates(0)

            // notify header instantly
            window.dispatchEvent(new Event('support-updates-read'))
        }
    }, [mainTab])


    const fetchTickets = async () => {
        setLoading(true)
        try {
            const response = await fetch(apiUrl('/portal/api/support/tickets/'), {
                method: 'GET',
                credentials: credentials(),
            })
            if (response.ok) {
                const data = await response.json()
                setTickets(data)
            } else {
                toast.error('Could not load tickets.')
            }
        } catch {
            toast.error('Could not load tickets.')
        } finally {
            setLoading(false)
        }
    }

    const fetchUpdates = async () => {
        try {
            const response = await fetch(apiUrl('/portal/api/support/updates/'), {
                method: 'GET',
                credentials: credentials(),
            })
            if (response.ok) {
                const data = await response.json()
                setUpdates(data)
            } else {
                toast.error('Could not load updates.')
            }
        } catch {
            toast.error('Could not load updates.')
        }
    }

    useEffect(() => { fetchTickets() }, [])
    useEffect(() => { fetchUpdates() }, [])

    // ── Ticket delete ──
    const handleDeleteTicket = async () => {
        if (!deletingTicket) return
        try {
            const csrfToken = await fetchCsrfToken()
            if (!csrfToken) { toast.error('CSRF error'); return }

            const res = await fetch(apiUrl(`/portal/api/support/tickets/${deletingTicket.ticket_id}/`), {
                method: 'DELETE',
                credentials: credentials(),
                headers: { 'X-CSRFToken': csrfToken },
            })

            if (res.ok) {
                toast.success('Ticket deleted.')
                setDeletingTicket(null)
                if (selectedTicket?.ticket_id === deletingTicket.ticket_id) setSelectedTicket(null)
                fetchTickets()
            } else {
                toast.error('Failed to delete ticket.')
            }
        } catch {
            toast.error('Failed to delete ticket.')
        }
    }

    // ── Update delete ──
    const handleDeleteUpdate = async () => {
        if (!deletingUpdate) return
        try {
            const csrfToken = await fetchCsrfToken()
            if (!csrfToken) { toast.error('CSRF error'); return }

            const res = await fetch(apiUrl(`/portal/api/support/updates/${deletingUpdate.update_id}/`), {
                method: 'DELETE',
                credentials: credentials(),
                headers: { 'X-CSRFToken': csrfToken },
            })

            if (res.ok) {
                toast.success('Update deleted.')
                setDeletingUpdate(null)
                fetchUpdates()
            } else {
                toast.error('Failed to delete update.')
            }
        } catch {
            toast.error('Failed to delete update.')
        }
    }

    const filteredTickets = filterStatus === 'All'
        ? tickets
        : tickets.filter(t => t.status === filterStatus)

    return (
        <div className="customer-directory-body">
            <ToastContainer position="bottom-right" />
            <TeamHeader title="Support" />

            <div className="support-container">

                {/* ── Page heading ── */}
                <div className="support-page-header">
                    <div>
                        <h1 className="support-page-title">Ticketing & Updates</h1>
                        <p className="support-page-sub">
                            Report issues, track progress, and stay informed.
                        </p>
                    </div>

                    <div className="main-tabs">
                        <button
                            className={`main-tab ${mainTab === 'tickets' ? 'active' : ''}`}
                            onClick={() => setMainTab('tickets')}
                        >
                            <span className="tab-icon">⊟</span>
                            Tickets
                            <span className="main-tab-count">{tickets.length}</span>
                        </button>

                        <button
                            className={`main-tab ${mainTab === 'updates' ? 'active' : ''}`}
                            onClick={() => setMainTab('updates')}
                        >
                            <span className="tab-icon">◈</span>

                            <span className="main-tab-label">
                                Team Updates
                            </span>

                            <span className="main-tab-count">{updates.length}</span>

                            {unreadUpdates > 0 && (
                                <span className="main-tab-notification-dot" />
                            )}
                        </button>
                    </div>
                </div>

                {/* ── Tickets Tab ── */}
                {mainTab === 'tickets' && (
                    <div className="tickets-layout">
                        <div className={`tickets-panel ${selectedTicket ? 'with-detail' : ''}`}>

                            <div className="tickets-toolbar">
                                <div className="tickets-subtabs">
                                    <button
                                        className={`sub-tab ${ticketsView === 'list' ? 'active' : ''}`}
                                        onClick={() => setTicketsView('list')}
                                    >
                                        {isAdmin ? 'All Tickets' : 'My Tickets'}
                                    </button>
                                    <button
                                        className={`sub-tab ${ticketsView === 'new' ? 'active' : ''}`}
                                        onClick={() => setTicketsView('new')}
                                    >
                                        + New
                                    </button>
                                </div>

                                {ticketsView === 'list' && (
                                    <div className="status-filters">
                                        {['All', ...STATUSES].map(s => (
                                            <button
                                                key={s}
                                                className={`filter-chip ${filterStatus === s ? 'active' : ''}`}
                                                onClick={() => setFilterStatus(s)}
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {ticketsView === 'list' ? (
                                <div className="ticket-list">
                                    {loading && <div className="loading-state">Loading tickets…</div>}

                                    {!loading && filteredTickets.length === 0 && (
                                        <div className="empty-state">
                                            No tickets found.
                                            <span className="empty-cta" onClick={() => setTicketsView('new')}>
                                                Submit one →
                                            </span>
                                        </div>
                                    )}

                                    {filteredTickets.map(t => (
                                        <TicketRow
                                            key={t.ticket_id}
                                            ticket={t}
                                            isAdmin={isAdmin}
                                            username={username ?? ''}
                                            first_name={first_name ?? ''}
                                            last_name={last_name ?? ''}
                                            onClick={() => {
                                                setSelectedTicket(prev =>
                                                    prev?.ticket_id === t.ticket_id ? null : t
                                                )
                                            }}
                                            onEdit={setEditingTicket}
                                            onDelete={setDeletingTicket}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <NewTicketForm
                                    onSuccess={() => {
                                        fetchTickets()
                                        setTicketsView('list')
                                    }}
                                />
                            )}
                        </div>

                        {selectedTicket && (
                            <TicketDetailPanel
                                ticket={selectedTicket}
                                onClose={() => setSelectedTicket(null)}
                            />
                        )}
                    </div>
                )}

                {/* ── Updates Tab ── */}
                {mainTab === 'updates' && (
                    <UpdatesTab
                        updates={updates}
                        isAdmin={isAdmin}
                        onEdit={setEditingUpdate}
                        onDelete={setDeletingUpdate}
                        onNew={() => setEditingUpdate('new')}
                    />
                )}
            </div>

            {/* ── Modals ── */}

            {editingTicket && (
                <TicketEditModal
                    ticket={editingTicket}
                    isAdmin={isAdmin}
                    onClose={() => setEditingTicket(null)}
                    onSaved={() => {
                        fetchTickets()
                        setSelectedTicket(null)
                    }}
                />
            )}

            {deletingTicket && (
                <ConfirmDeleteModal
                    message={`Delete ticket #${deletingTicket.ticket_id} "${deletingTicket.title}"? This cannot be undone.`}
                    onConfirm={handleDeleteTicket}
                    onCancel={() => setDeletingTicket(null)}
                />
            )}

            {editingUpdate !== null && (
                <UpdateModal
                    update={editingUpdate === 'new' ? null : editingUpdate}
                    onClose={() => setEditingUpdate(null)}
                    onSaved={fetchUpdates}
                />
            )}

            {deletingUpdate && (
                <ConfirmDeleteModal
                    message={`Delete update "${deletingUpdate.title}"? This cannot be undone.`}
                    onConfirm={handleDeleteUpdate}
                    onCancel={() => setDeletingUpdate(null)}
                />
            )}
        </div>
    )
}

export default SupportMain